import { GridColumn as Column } from '@progress/kendo-react-grid';
import { sortBy } from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RouteAnimation } from 'src/animations/animations';
import {
  DeleteButton,
  SecondaryButton
} from 'src/components/shared/Button/Button';
import { DataGrid } from 'src/components/shared/DataGrid/DataGrid';
import { GridCountCell } from 'src/components/shared/DataGrid/GridCountCell';
import {
  HeaderSection,
  HeaderSectionLeft,
  HeaderSectionRight
} from 'src/components/shared/HeaderSection/HeaderSection';
import { Modal } from 'src/components/shared/Modal/Modal';
import { BodyType } from 'src/components/shared/Typography/BodyType';
import { Header1 } from 'src/components/shared/Typography/Header1';
import { useReduxDispatch } from 'src/store';
import { ParameterSetDto } from 'src/store/parametersets/parameterset';
import {
  deleteParameterSet,
  fetchParameterSets
} from 'src/store/parametersets/parametersets.actions';
import { ParameterSetApi } from 'src/store/parametersets/parametersets.api';
import { selectParameterSets } from 'src/store/parametersets/parametersets.selectors';
import { isLoadingParameterSets } from 'src/store/requests/requests.selectors';
import { push } from 'src/utils/navigation';

export const ParameterSetsPage = () => {
  const [currentParameterSet, setCurrentParameterSet] =
    useState<Nullable<ParameterSetDto>>(null);
  const [parameterSet, setParameterSet] =
    useState<Nullable<ParameterSetDto>>(null);
  const [parameterSets, setParameterSets] = useState<ParameterSetDto[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const { t } = useTranslation();
  const dispatch = useReduxDispatch();
  const navigate = useNavigate();

  const parameterSets$ = useSelector(selectParameterSets);
  const loadingParameterSets = useSelector(isLoadingParameterSets);

  useEffect(() => {
    dispatch(fetchParameterSets());
  }, [dispatch]);

  useEffect(() => {
    setParameterSets(parameterSets$);
  }, [parameterSets$]);

  useEffect(() => {
    setParameterSets(
      parameterSets.map(parameterSet =>
        parameterSet.id === currentParameterSet?.id
          ? {
              ...parameterSet,
              expanded: true,
              parameterSetParameters: sortBy(
                currentParameterSet?.parameterSetParameters ?? [],
                parameter => parameter?.column,
                'column'
              )
            }
          : parameterSet
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentParameterSet]);

  const onCreate = () => {
    push('/parametersets/create', navigate, RouteAnimation.SlideRight);
  };

  const onEdit = async (parameterSet: ParameterSetDto) => {
    push(
      `/parametersets/${parameterSet.id}`,
      navigate,
      RouteAnimation.SlideRight
    );
  };

  const onCancel = () => {
    setParameterSet(null);
    setShowDeleteModal(false);
  };

  const onDelete = (parameterSet: ParameterSetDto) => {
    setParameterSet(parameterSet);
    setShowDeleteModal(true);
  };

  const onConfirmDelete = async () => {
    if (parameterSet) {
      await dispatch(deleteParameterSet(parameterSet.id, parameterSet));
      setParameterSet(null);
      setShowDeleteModal(false);
    }
  };

  const onParameterSetDetailClick = async (
    parameterSet: ParameterSetDto & Expandable
  ) => {
    if (parameterSet.expanded) {
      setParameterSets(
        parameterSets.map(parameterSet =>
          parameterSet.id === parameterSet.id
            ? {
                ...parameterSet,
                expanded: false
              }
            : parameterSet
        )
      );
    } else {
      const response = await ParameterSetApi.getById(parameterSet.id);
      if (response.ok) {
        setCurrentParameterSet(response.data);
      }
    }
  };

  return (
    <>
      <HeaderSection className="mb-32">
        <HeaderSectionLeft>
          <Header1>{t('parameterSet.plural')}</Header1>
          <BodyType>{`${t('crudtext')} ${t('parameterSet.plural')}`}</BodyType>
        </HeaderSectionLeft>
        <HeaderSectionRight>
          <SecondaryButton onClick={onCreate} icon="la-plus">
            {`${t('create.verb')} ${t('parameterSet.singular')}`}
          </SecondaryButton>
        </HeaderSectionRight>
      </HeaderSection>

      <DataGrid
        items={parameterSets}
        loading={loadingParameterSets}
        detail={props => (
          <DataGrid
            items={props.dataItem.parameterSetParameters}
            style={{ marginTop: 20 }}
          >
            <Column field="column" title={t('props.column')} />
            <Column field="parameterName" title={t('parameter.singular')} />
            <Column field="methodName" title={t('method.singular')} />
          </DataGrid>
        )}
        expandField="expanded"
        onRowClick={event => onEdit(event.dataItem)}
        onExpandChange={event => onParameterSetDetailClick(event.dataItem)}
      >
        <Column
          field="no"
          title={t('props.number')}
          width="120px"
          footerCell={() => (
            <GridCountCell
              count={parameterSets?.length}
              label={t('parameterSet.plural')}
            />
          )}
        />
        <Column field="name" title={t('props.name')} />
        <Column
          field="parameterSetTypeName"
          title={t('props.type')}
          width="180px"
        />
        <Column field="username" title={t('props.username')} width="180px" />
        <Column
          width="100px"
          filterable={false}
          cell={props => (
            <td>
              <DeleteButton onClick={() => onDelete(props.dataItem)} />
            </td>
          )}
        />
      </DataGrid>

      <Modal
        width={500}
        header={`${t('delete.verb')} ${t('parameterSet.singular')}`}
        confirmText={t('delete.verb')}
        onClose={() => onCancel()}
        onConfirm={() => onConfirmDelete()}
        open={showDeleteModal}
        showActions
      >
        <BodyType>
          {t('parameterSet.delete')} <q>{parameterSet?.name}</q>?
        </BodyType>
      </Modal>
    </>
  );
};
